import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Smartphone, Apple, Send } from "lucide-react";
import { GlassCard, SectionHeading } from "./GlassCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DownloadCTA = () => {
  const [form, setForm] = useState({ name: "", business_name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    axios.get(`${API}/app-meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please fill in your name and phone number");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Shukriya! Our team will reach out on WhatsApp within 24 hours.");
      setForm({ name: "", business_name: "", phone: "" });
    } catch {
      toast.error("Something went wrong. Please WhatsApp us at +91 8651938731.");
    } finally {
      setLoading(false);
    }
  };

  const platformLine = (p, fallback) =>
    meta?.[p]
      ? `v${meta[p].version} · ${meta[p].size_mb} MB · ${meta[p].filename || (p === "android" ? "gromza-android.apk" : "gromza-ios.ipa")}${meta[p].downloads > 0 ? ` · ${meta[p].downloads.toLocaleString("en-IN")} downloads` : ""}`
      : fallback;

  return (
    <section id="download" data-testid="download-section" className="relative py-24 lg:py-36">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHeading
            testId="download-heading"
            eyebrow="Get Gromza"
            title="Aaj hi apna business"
            highlight="list karein."
            description="Download the app directly from Gromza — no third-party stores, no redirects. Or leave your details and our team will set up your digital storefront personally."
          />
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="/api/download/apk-android"
              data-testid="download-android-button"
              className="inline-flex items-center gap-3 rounded-2xl bg-amber-400 px-7 py-4 text-[#0b0f19] shadow-[0_0_36px_rgba(251,191,36,0.35)] hover:bg-amber-300 hover:shadow-[0_0_56px_rgba(251,191,36,0.55)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300"
            >
              <Smartphone size={22} />
              <span className="text-left">
                <span className="block text-[10px] font-medium uppercase tracking-wider opacity-70" data-testid="android-download-stats">
                  {platformLine("android", "v1.3 · 99 MB")}
                </span>
                <span className="block text-sm font-bold">Android APK</span>
              </span>
            </a>
            {(() => {
              const ios = meta?.ios;
              const iosReady = ios && ios.size_mb >= 1;
              return iosReady ? (
                <a
                  href="/api/download/apk-ios"
                  data-testid="download-ios-button"
                  className="inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-xl px-7 py-4 hover:border-amber-400/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-300"
                >
                  <Apple size={22} />
                  <span className="text-left">
                    <span className="block text-[10px] font-medium uppercase tracking-wider text-slate-400" data-testid="ios-download-stats">
                      {platformLine("ios", "v1.3 · 112 MB")}
                    </span>
                    <span className="block text-sm font-bold">iOS IPA</span>
                  </span>
                </a>
              ) : (
                <div
                  data-testid="download-ios-button"
                  className="inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-7 py-4 cursor-not-allowed select-none"
                >
                  <Apple size={22} className="text-slate-500" />
                  <span className="text-left">
                    <span className="block text-[10px] font-medium uppercase tracking-wider text-slate-500" data-testid="ios-download-stats">
                      iPhone ke liye jald aa raha hai
                    </span>
                    <span className="block text-sm font-bold text-slate-400">iOS App</span>
                  </span>
                  <span className="ml-2 rounded-full bg-amber-400/15 border border-amber-400/30 px-3 py-1 text-[10px] font-bold tracking-widest text-amber-300" data-testid="ios-coming-soon-badge">
                    COMING SOON
                  </span>
                </div>
              );
            })()}
          </div>
        </div>

        <GlassCard testId="partner-form-card" className="p-9">
          <div id="partner">
            <h3 className="font-display text-2xl font-semibold mb-2">
              Become a <span className="gold-text">Gromza Partner</span>
            </h3>
            <p className="text-sm text-slate-400 mb-8">Free onboarding · 90-day trial · dedicated support</p>
            <form onSubmit={submit} className="space-y-5" data-testid="partner-lead-form">
              <input
                data-testid="lead-name-input"
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-5 py-3.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.06] transition-[border-color,background-color] duration-300"
              />
              <input
                data-testid="lead-business-input"
                type="text"
                placeholder="Business / shop name"
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-5 py-3.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.06] transition-[border-color,background-color] duration-300"
              />
              <input
                data-testid="lead-phone-input"
                type="tel"
                placeholder="WhatsApp number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-5 py-3.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.06] transition-[border-color,background-color] duration-300"
              />
              <button
                data-testid="lead-submit-button"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-amber-400 px-8 py-4 text-sm font-semibold text-[#0b0f19] hover:bg-amber-300 hover:shadow-[0_0_40px_rgba(251,191,36,0.4)] disabled:opacity-60 transition-[background-color,box-shadow] duration-300"
              >
                {loading ? "Sending..." : <>Request Callback <Send size={15} /></>}
              </button>
            </form>
          </div>
        </GlassCard>
      </div>
    </section>
  );
};
